import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Lottie from "lottie-react";
import certificateAnimation from "../../resources/json/certificate-loading.json";
import "./FullScreenLoadingOverlay.css";

const STATUS_KEYS = [
  "certificateOnboarding:overlay_preparing",
  "certificateOnboarding:overlay_validating",
  "certificateOnboarding:overlay_setting_up",
  "certificateOnboarding:overlay_almost_ready",
];

const COMPLETE_KEY = "certificateOnboarding:overlay_complete";

const MESSAGE_CYCLE_MS = 2000;
const PROGRESS_CRAWL_MAX = 92;
const PROGRESS_CRAWL_DURATION_MS = 4000;
const PROGRESS_TICK_MS = 50;
const RUSH_TO_100_MS = 500;
const HOLD_COMPLETE_MS = 600;
const EXIT_ANIMATION_MS = 300;

const PHASE = {
  HIDDEN: "hidden",
  LOADING: "loading",
  COMPLETING: "completing",
  EXITING: "exiting",
};

function FullScreenLoadingOverlay({ isVisible, statusMessage }) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState(PHASE.HIDDEN);
  const [msgIndex, setMsgIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const rushStartRef = useRef(null);
  const rushStartProgressRef = useRef(0);
  const previousBodyOverflowRef = useRef("");
  const holdCompleteTimerRef = useRef(null);

  useEffect(() => {
    if (isVisible && phase === PHASE.HIDDEN) {
      setProgress(0);
      setMsgIndex(0);
      setPhase(PHASE.LOADING);
      previousBodyOverflowRef.current = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    } else if (!isVisible && phase === PHASE.LOADING) {
      rushStartRef.current = Date.now();
      rushStartProgressRef.current = progress;
      setPhase(PHASE.COMPLETING);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, phase]);

  useEffect(() => {
    if (phase !== PHASE.LOADING) return;

    const step =
      (PROGRESS_CRAWL_MAX / PROGRESS_CRAWL_DURATION_MS) * PROGRESS_TICK_MS;
    const progressInterval = setInterval(() => {
      setProgress((p) => {
        const next = p + step;
        return next >= PROGRESS_CRAWL_MAX ? PROGRESS_CRAWL_MAX : next;
      });
    }, PROGRESS_TICK_MS);

    const msgInterval = setInterval(() => {
      setMsgIndex((i) => (i + 1) % STATUS_KEYS.length);
    }, MESSAGE_CYCLE_MS);

    return () => {
      clearInterval(progressInterval);
      clearInterval(msgInterval);
    };
  }, [phase]);

  useEffect(() => {
    if (phase !== PHASE.COMPLETING) return;

    const startProgress = rushStartProgressRef.current;
    const remaining = 100 - startProgress;

    const interval = setInterval(() => {
      const elapsed = Date.now() - rushStartRef.current;
      const ratio = Math.min(elapsed / RUSH_TO_100_MS, 1);
      const eased = 1 - Math.pow(1 - ratio, 3);
      const next = startProgress + remaining * eased;

      setProgress(next);

      if (ratio >= 1) {
        clearInterval(interval);
        setProgress(100);
        holdCompleteTimerRef.current = setTimeout(() => {
          setPhase(PHASE.EXITING);
        }, HOLD_COMPLETE_MS);
      }
    }, 16);

    return () => {
      clearInterval(interval);
      if (holdCompleteTimerRef.current) clearTimeout(holdCompleteTimerRef.current);
    };
  }, [phase]);

  useEffect(() => {
    if (phase !== PHASE.EXITING) return;

    const timer = setTimeout(() => {
      setPhase(PHASE.HIDDEN);
      document.body.style.overflow = previousBodyOverflowRef.current;
    }, EXIT_ANIMATION_MS);

    return () => clearTimeout(timer);
  }, [phase]);

  useEffect(() => {
    return () => {
      document.body.style.overflow = previousBodyOverflowRef.current;
    };
  }, []);

  const isEntering = phase === PHASE.LOADING || phase === PHASE.COMPLETING;
  const isExiting = phase === PHASE.EXITING;
  const isComplete = phase === PHASE.COMPLETING && progress >= 99.5;

  const displayMessage =
    statusMessage ||
    (isComplete ? t(COMPLETE_KEY) : t(STATUS_KEYS[msgIndex]));

  if (phase === PHASE.HIDDEN) return null;

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center pointer-events-auto bg-cfOverlayBackdrop backdrop-blur-md cert-overlay-backdrop ${isExiting ? "animate-cert-overlay-out" : "animate-cert-overlay-in"
        }`}
    >
      <div
        className={`relative z-10 flex flex-col items-center w-[90vw] max-w-sm rounded-2xl p-8 shadow-2xl bg-cfOverlayCardBg  cert-overlay-card ${isExiting ? "animate-cert-card-out" : "animate-cert-card-in"
          }`}
      >
        <div className="w-40 h-40 mb-4">
          <Lottie
            animationData={certificateAnimation}
            loop
            autoplay
            style={{ width: "100%", height: "100%" }}
          />
        </div>

        <p
          className="text-white text-sm font-medium text-center mb-5 h-5 animate-cert-msg-fade"
          key={isComplete ? "complete" : msgIndex}
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {displayMessage}
        </p>

        <div className="w-full h-1.5 rounded-full bg-cfOverlayProgressTrack overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{
              width: `${progress}%`,
              background: isComplete
                ? `linear-gradient(90deg, var(--cf-active-text-color), var(--cf-active-border-color))`
                : `linear-gradient(90deg, var(--cf-primary-color), var(--cf-primary-light-color))`,
              transition: isEntering
                ? `width ${PROGRESS_TICK_MS}ms linear, background-color 0.3s ease`
                : "none",
            }}
          />
        </div>

        <p
          className={`text-xs mt-2 font-mono tabular-nums transition-colors duration-300 ${isComplete ? "text-cfActiveText" : "text-cfOverlayTextMuted"
            }`}
          aria-hidden="true"
        >
          {Math.round(progress)}%
        </p>
      </div>
    </div>
  );
}

export default FullScreenLoadingOverlay;
